import React from 'react';

import { Col } from '@opendoor/atoms/box';
import { Dialog } from '@opendoor/atoms/dialog';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';

import type { GetLogs } from '../graphql/queries/__generated__/GetLogs';

type GetLogs_getLogs_logs = GetLogs['getLogs']['logs'][number];
import { PressableTableRow, Table, TableCell, TableRow } from './Table';

interface ILogProps {
  data: GetLogs_getLogs_logs;
  onClick: (log: GetLogs_getLogs_logs) => void;
}

interface ILogCellProps {
  children: string | React.ReactElement;
}

interface IStatusBadgeProps {
  status: string;
}

const LogCell = ({ children }: ILogCellProps) => {
  return <TableCell>{children}</TableCell>;
};

const StatusBadge = ({ status }: IStatusBadgeProps) => {
  const textColor = status === 'SUCCESS' ? 'text-od-content-success' : 'text-od-content-error';
  return (
    // pl-1 stands in for the old marginLeft: Atoms does not generate margin
    // utilities, and this badge has no background, so padding reserves the same space.
    <Text as="span" className={cn('text-label-md pl-1 font-bold', textColor)}>
      {status.split('_').join(' ')}
    </Text>
  );
};

export const LogRow = ({ data, onClick }: ILogProps) => {
  const { statusCode, operationType, method, path, timestamp } = data;
  const handlePress = () => {
    onClick(data);
  };

  const ariaLabel = `${statusCode} ${
    operationType ? operationType.toUpperCase() : method
  } ${path} at ${new Date(parseInt(timestamp, 10)).toLocaleString()}`;

  return (
    <PressableTableRow onClick={handlePress} analyticsName="usage-log-row" aria-label={ariaLabel}>
      <LogCell>
        <StatusBadge status={statusCode} />
      </LogCell>
      <LogCell>
        <span>
          {operationType ? operationType.toUpperCase() : method}
          &nbsp;&nbsp;&nbsp;&nbsp;
          {path}
        </span>
      </LogCell>
      <LogCell>{new Date(parseInt(timestamp, 10)).toLocaleString()}</LogCell>
    </PressableTableRow>
  );
};

interface ILogViewProps {
  isOpen: boolean;
  logData: GetLogs_getLogs_logs;
  setIsOpen: (open: boolean) => void;
}

interface IJsonDisplayProps {
  data: string;
}

const safeParseJson = (input: string): string => {
  if (!input || input.trim() === '') {
    return '';
  }

  try {
    const parsed = JSON.parse(input);
    return JSON.stringify(parsed, null, 2);
  } catch {
    // Return original string if it's not valid JSON
    return input;
  }
};

const JsonDisplay: React.FC<IJsonDisplayProps> = ({ data }) => {
  return (
    <Col className="overflow-x-auto rounded bg-od-bg-secondary p-2.5">
      <Text
        as="span"
        className="text-body-sm block m-0 whitespace-pre-wrap break-words font-mono text-od-content-primary"
      >
        {data}
      </Text>
    </Col>
  );
};

export const LogViewer = ({ isOpen, logData, setIsOpen }: ILogViewProps) => {
  const description = logData.operationType
    ? logData.operationType.toUpperCase() + ' ' + logData.path
    : logData.method + ' ' + logData.path;

  return (
    <Dialog.Root role="dialog" open={isOpen} onOpenChange={setIsOpen}>
      {/* The panel sizes itself from its content and has no height cap of its own, and
          because it centers via `left-1/2` its shrink-to-fit width tops out at 50vw. A
          log body is arbitrarily long, so pin both axes and scroll the content below. */}
      <Dialog className="flex max-h-[90dvh] flex-col sm:w-[65vw]">
        {/* The heading is a request path, not a headline: hold it at body size instead of
            the display-scale default, which wraps a typical path across two 45px lines. */}
        <Dialog.Title className="text-body-md pb-4 sm:pb-6 sm:pr-8 sm:text-body-md">
          {description}
        </Dialog.Title>
        <Col className="min-h-0 overflow-y-auto">
          <Col className="pt-1">
            <Text as="p" className="text-label-md font-bold">
              Details
            </Text>
            <Table columns="auto 1fr">
              <TableRow>
                <LogCell>Time</LogCell>
                <LogCell>{new Date(parseInt(logData.timestamp, 10)).toLocaleString()}</LogCell>
              </TableRow>
              <TableRow>
                <LogCell>Status</LogCell>
                <LogCell>{logData.statusCode}</LogCell>
              </TableRow>
              <TableRow>
                <LogCell>Method</LogCell>
                <LogCell>{logData.method}</LogCell>
              </TableRow>
              {logData.operationType ? (
                <TableRow>
                  <LogCell>GraphQL Operation</LogCell>
                  <LogCell>{logData.operationType.toUpperCase()}</LogCell>
                </TableRow>
              ) : null}
              <TableRow>
                <LogCell>Path</LogCell>
                <LogCell>{logData.path}</LogCell>
              </TableRow>
              {logData.origin && (
                <TableRow>
                  <LogCell>Origin</LogCell>
                  <LogCell>{logData.origin}</LogCell>
                </TableRow>
              )}
              {logData.agent && (
                <TableRow>
                  <LogCell>Device</LogCell>
                  <LogCell>{logData.agent}</LogCell>
                </TableRow>
              )}
            </Table>
          </Col>
          <Col className="pt-1">
            <Text as="p" className="text-label-md font-bold">
              Request Body
            </Text>
            <JsonDisplay data={safeParseJson(logData.requestBody)} />
          </Col>
          <Col className="pt-1">
            <Text as="p" className="text-label-md font-bold">
              Response Body
            </Text>
            <JsonDisplay data={safeParseJson(logData.responseBody)} />
          </Col>
        </Col>
      </Dialog>
    </Dialog.Root>
  );
};
